import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatbotAskApi, chatbotHealthApi, type ChatbotHealth, type FeeData } from './chatbotApi';
import {
  loadHistory, saveHistory, appendMessage, clearHistory, toApiHistory,
  type StoredHistory,
} from './useChatbotHistory';
import logo from '../../assets/logo.jpg';
import './HomeChatbot.scss';

/**
 * HomeChatbot — floating AI assistant for the public landing page.
 *
 * Backed by /api/chatbot/ask (RAG over JSONL knowledge base + DeepSeek).
 * Multi-turn clarification flow: when the bot needs to know which
 * branch/unit the user is asking about, it returns clickable option
 * chips and threads a sessionToken so the next question is filtered
 * to the picked unit.
 *
 * History is persisted in localStorage with a 7-day TTL — see
 * useChatbotHistory.ts. Last 10 messages are sent as context with
 * each ask so DeepSeek can keep the conversation coherent.
 *
 * UX features:
 * - Empty-state probe: GET /api/chatbot/health on mount; when bot has
 *   no training data, the welcome message and quick-actions are
 *   suppressed so we don't promise answers we can't deliver.
 * - Markdown rendering: bot replies pass through react-markdown +
 *   remark-gfm (lists, links, bold, tables) — operator-typed
 *   knowledge often uses bullet lists.
 * - Retry button: when an ask fails, the error bubble shows a 🔁 chip
 *   that re-sends the last user question.
 * - Simulated streaming: bot answers are revealed at ~18ms per
 *   character so the panel never sits frozen on a wall of text.
 *
 * Design source: D:/DriveHub/UI-TEST/chatAI (Cognitive Core tokens —
 * navy primary + green accent, 16px radius, Inter font).
 */

type Role = 'bot' | 'user';

type Message = {
  id: string;
  role: Role;
  text: string;
  /** Substring of `text` actually rendered. When set, the bubble shows
   *  only the first `streamedLength` chars and the rest reveals at the
   *  typing speed. Stored as a separate field so React rerenders the
   *  bubble (the source `text` is captured in history all at once). */
  streamedLength?: number;
  card?: { title: string; meta: string; icon?: string };
  options?: string[];
  sources?: string[];
  /** When true, this is an error bubble — render a retry chip that
   *  re-sends `failedQuery` as a user message. */
  error?: boolean;
  failedQuery?: string;
  feeData?: FeeData | null;
};

const NOW_LABEL = (() => {
  try {
    const d = new Date();
    return `Hôm nay, ${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return 'Hôm nay';
  }
})();

const WELCOME_READY: Message = {
  id: 'm1',
  role: 'bot',
  text: 'Xin chào! Tôi là Trợ lý AI của Lái Xe Trung Bộ. Mình có thể giúp gì cho bạn về việc học và thi GPLX hôm nay?',
};

const WELCOME_EMPTY: Message = {
  id: 'm1',
  role: 'bot',
  text: 'Xin chào! Trợ lý AI của Lái Xe Trung Bộ đang được cập nhật dữ liệu. Mình sẽ sớm có thể trả lời các câu hỏi về học và thi GPLX. Bạn vui lòng quay lại sau nhé.',
};

const QUICK_ACTIONS = [
  { id: 'q1', text: 'Lịch thi sắp tới?', accent: true },
  { id: 'q2', text: 'Đóng phí thi lại như thế nào?', accent: false },
  { id: 'q3', text: 'Hạng B cần giấy tờ gì?', accent: false },
  { id: 'q4', text: 'Lệ phí thi tham khảo?', accent: false },
  { id: 'q5', text: 'Địa chỉ trung tâm?', accent: false },
  { id: 'q6', text: 'Thời gian học hạng C?', accent: false },
  { id: 'q7', text: 'Quy định thi sa hình?', accent: false },
  { id: 'q8', text: 'Hồ sơ nâng hạng B2 → C?', accent: false },
];

const TYPING_SPEED_MS = 18; // ms per character — feels close to live streaming
const RATE_LIMIT_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX = 60;

let counter = 100;
const newId = () => `m${counter++}`;

const HomeChatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_READY]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<ChatbotHealth | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Drag-to-scroll state for the horizontal quick-actions strip. We
  // suppress chip clicks when the pointer moved more than DRAG_THRESHOLD
  // pixels, so a real drag doesn't accidentally fire send().
  const quickActionsRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<{
    active: boolean;
    startX: number;
    scrollLeft: number;
    moved: number;
  }>({ active: false, startX: 0, scrollLeft: 0, moved: 0 });
  // Track in-flight typing animations so we can cancel them when the
  // user clears the chat or kicks off a new question mid-reveal.
  const typingTimers = useRef<Set<number>>(new Set());
  // Sliding-window rate limiter — keeps the user under 60 sends per
  // 60 s. We allow multi-in-flight (no `loading` block on input), so
  // this is the only guardrail against accidental floods.
  const sendTimestamps = useRef<number[]>([]);
  // Mirror of localStorage. Read once on mount; mutated via the
  // helpers in useChatbotHistory.ts so persistence stays in one place.
  const historyRef = useRef<StoredHistory>(loadHistory());

  // Replay persisted history on first mount so the operator sees their
  // conversation across page reloads (within 7-day TTL).
  useEffect(() => {
    const h = historyRef.current;
    if (h.messages.length > 0) {
      setMessages([
        WELCOME_READY,
        ...h.messages.map((m) => ({
          id: m.id,
          role: m.role,
          text: m.text,
          card: m.card,
          options: m.options,
        } as Message)),
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Probe BE for training-data presence so we can pick a welcome message
  // and decide whether to show quick-action chips. Failure is non-fatal
  // — we just keep the optimistic "ready" welcome.
  useEffect(() => {
    let cancelled = false;
    chatbotHealthApi()
      .then((resp) => {
        if (cancelled || resp.EC !== 0 || !resp.DT) return;
        setHealth(resp.DT);
        // Only swap the seed welcome if the user hasn't started chatting.
        if (historyRef.current.messages.length === 0) {
          setMessages([resp.DT.hasData ? WELCOME_READY : WELCOME_EMPTY]);
        }
      })
      .catch(() => { /* network down — stay optimistic */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-scroll on new message + open transition + each typing tick.
  useEffect(() => {
    if (!open || !bodyRef.current) return;
    bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open, loading]);

  // Focus input after opening so the operator can type immediately.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Cancel any pending typing timers on unmount so React doesn't warn
  // about state updates on an unmounted component.
  useEffect(() => {
    const timersAtMount = typingTimers.current;
    return () => {
      timersAtMount.forEach((t) => window.clearTimeout(t));
      timersAtMount.clear();
    };
  }, []);

  // Auto-resize textarea up to max-height (CSS handles the cap).
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  /** Reveal message text one character at a time so the bubble doesn't
   *  appear instantly. Cancellable via the typingTimers ref. */
  const startTypingReveal = (msgId: string, fullText: string) => {
    const total = fullText.length;
    const step = (i: number) => {
      setMessages((prev) => prev.map((m) => (
        m.id === msgId ? { ...m, streamedLength: i } : m
      )));
      if (i >= total) return;
      const t = window.setTimeout(() => {
        typingTimers.current.delete(t);
        step(i + 1);
      }, TYPING_SPEED_MS);
      typingTimers.current.add(t);
    };
    step(1);
  };

  const send = async (text?: string) => {
    const value = (text ?? input).trim();
    if (!value) return;

    // Sliding-window cap so the bot can serve up to 60 sends per 60s
    // even when multiple are in flight, without unbounded flooding.
    const now = Date.now();
    sendTimestamps.current = sendTimestamps.current.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
    if (sendTimestamps.current.length >= RATE_LIMIT_MAX) {
      const oldest = sendTimestamps.current[0];
      const waitSec = Math.ceil((RATE_LIMIT_WINDOW_MS - (now - oldest)) / 1000);
      const limitMsg: Message = {
        id: newId(),
        role: 'bot',
        text: `Bạn đang gửi quá nhanh — đã đạt ${RATE_LIMIT_MAX} câu/phút. Chờ ~${waitSec}s nhé.`,
      };
      setMessages((prev) => [...prev, limitMsg]);
      return;
    }
    sendTimestamps.current.push(now);

    // Cancel any in-flight typing animation — new question starts fresh.
    typingTimers.current.forEach((t) => window.clearTimeout(t));
    typingTimers.current.clear();

    const userMsg: Message = { id: newId(), role: 'user', text: value };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';

    historyRef.current = appendMessage(historyRef.current, {
      id: userMsg.id, role: 'user', text: value,
    });
    saveHistory(historyRef.current);

    setLoading(true);
    try {
      const resp = await chatbotAskApi({
        question: value,
        sessionToken: historyRef.current.sessionToken,
        history: toApiHistory(historyRef.current),
      });
      if (resp.EC !== 0 || !resp.DT) {
        const errMsg: Message = {
          id: newId(), role: 'bot',
          text: resp.EM || 'Hệ thống AI tạm thời chưa sẵn sàng. Vui lòng thử lại sau.',
          error: true,
          failedQuery: value,
        };
        setMessages((prev) => [...prev, errMsg]);
        return;
      }
      const dt = resp.DT;
      const replyId = newId();
      const reply: Message = dt.type === 'clarify'
        ? {
            id: replyId,
            role: 'bot',
            text: dt.text,
            options: dt.options,
            streamedLength: 0,
          }
        : {
            id: replyId,
            role: 'bot',
            text: dt.text,
            sources: dt.sources,
            streamedLength: 0,
            feeData: dt.feeData ?? null,
          };
      setMessages((prev) => [...prev, reply]);
      startTypingReveal(replyId, dt.text);

      historyRef.current = appendMessage(historyRef.current, {
        id: reply.id, role: 'bot', text: reply.text,
        options: reply.options,
      });
      historyRef.current.sessionToken = dt.sessionToken || historyRef.current.sessionToken;
      saveHistory(historyRef.current);
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } }; message?: string };
      const fallback: Message = {
        id: newId(), role: 'bot',
        text: e?.response?.data?.EM || 'Mạng đang chậm. Bạn thử lại giúp mình nhé.',
        error: true,
        failedQuery: value,
      };
      setMessages((prev) => [...prev, fallback]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const clearChat = () => {
    typingTimers.current.forEach((t) => window.clearTimeout(t));
    typingTimers.current.clear();
    setMessages([health && !health.hasData ? WELCOME_EMPTY : WELCOME_READY]);
    setInput('');
    historyRef.current = clearHistory();
  };

  const showQuickActions = !health || health.hasData;

  // ─── Drag-to-scroll for the quick-actions chip strip ──────────────
  // Click suppression threshold — if pointer moves more than this many
  // pixels during a drag, the subsequent chip click is ignored.
  const DRAG_THRESHOLD_PX = 6;

  const onChipsPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = quickActionsRef.current;
    if (!el) return;
    dragState.current = {
      active: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      moved: 0,
    };
    // Note: intentionally NOT calling setPointerCapture here — capturing the
    // pointer on the parent container hijacks the chip's native click event,
    // which broke "tap to send" on the suggested-question chips. We rely on
    // the `moved` accumulator below to distinguish a real drag from a click.
  };

  const onChipsPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const ds = dragState.current;
    if (!ds.active) return;
    const el = quickActionsRef.current;
    if (!el) return;
    const dx = e.clientX - ds.startX;
    ds.moved = Math.max(ds.moved, Math.abs(dx));
    if (ds.moved > 0) el.classList.add('is-dragging');
    el.scrollLeft = ds.scrollLeft - dx;
  };

  const onChipsPointerEnd = () => {
    const el = quickActionsRef.current;
    if (el) el.classList.remove('is-dragging');
    // Keep `moved` set so the click handler can read it; cleared on next pointerdown.
    dragState.current.active = false;
  };

  const onChipClick = (text: string) => (e: React.MouseEvent) => {
    if (dragState.current.moved > DRAG_THRESHOLD_PX) {
      // User dragged the strip; suppress the click that the browser
      // synthesises when the pointer-up lands on this chip.
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    send(text);
  };

  return (
    <div className="home-chatbot" aria-live="polite">
      {open && (
        <div className="home-chatbot__panel" role="dialog" aria-label="Trợ lý AI">
          {/* Header — gradient navy with avatar + title + close/clear */}
          <div className="home-chatbot__header">
            <div className="home-chatbot__header-info">
              <div className="home-chatbot__header-avatar">
                <img src={logo} alt="Logo Lái Xe Trung Bộ" />
                <span className="home-chatbot__online-dot" aria-hidden="true" />
              </div>
              <div className="home-chatbot__header-text">
                <h2 className="home-chatbot__header-title">Trợ lý Lái Xe Trung Bộ</h2>
                <p className="home-chatbot__header-subtitle">
                  {health && !health.hasData ? 'Đang cập nhật dữ liệu' : 'Sẵn sàng hỗ trợ'}
                </p>
              </div>
            </div>
            <div className="home-chatbot__header-actions">
              <button
                type="button"
                className="home-chatbot__header-btn"
                onClick={clearChat}
                aria-label="Xoá hội thoại"
                title="Xoá hội thoại"
              >
                <i className="material-icons">cleaning_services</i>
              </button>
              <button
                type="button"
                className="home-chatbot__header-btn"
                onClick={() => setOpen(false)}
                aria-label="Đóng"
                title="Đóng"
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          </div>

          {/* Body — message list */}
          <div className="home-chatbot__body" ref={bodyRef}>
            <div className="home-chatbot__time-divider">{NOW_LABEL}</div>

            {messages.map((m, idx) => {
              const prev = messages[idx - 1];
              const continuation = !!prev && prev.role === m.role;
              if (m.role === 'bot') {
                const visibleText = typeof m.streamedLength === 'number'
                  ? m.text.slice(0, m.streamedLength)
                  : m.text;
                const stillTyping = typeof m.streamedLength === 'number' && m.streamedLength < m.text.length;
                return (
                  <div
                    key={m.id}
                    className={`home-chatbot__msg home-chatbot__msg--bot${continuation ? ' home-chatbot__msg--continuation' : ''}`}
                  >
                    {!continuation && (
                      <div className="home-chatbot__msg-avatar home-chatbot__msg-avatar--bot">
                        <img src={logo} alt="Bot" />
                      </div>
                    )}
                    {continuation && <div style={{ width: 24, flexShrink: 0 }} />}
                    <div className={`home-chatbot__msg-bubble home-chatbot__msg-bubble--bot${m.error ? ' home-chatbot__msg-bubble--error' : ''}`}>
                      <div className="home-chatbot__msg-markdown">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Force links to open in a new tab — public-facing
                            // panel, no SPA routing across origins.
                            a: ({ node, ...props }) => (
                              <a {...props} target="_blank" rel="noopener noreferrer" />
                            ),
                          }}
                        >
                          {visibleText || (stillTyping ? '​' : '')}
                        </ReactMarkdown>
                        {stillTyping && <span className="home-chatbot__cursor" aria-hidden="true" />}
                      </div>
                      {m.card && (
                        <div className="home-chatbot__msg-card">
                          <p className="home-chatbot__msg-card-title">{m.card.title}</p>
                          <p className="home-chatbot__msg-card-meta">
                            <i className="material-icons">{m.card.icon || 'info'}</i>
                            {m.card.meta}
                          </p>
                        </div>
                      )}
                      {/* Clarification chips — only after the typing reveal
                          finishes, so they don't pop in mid-sentence. */}
                      {m.options && m.options.length > 0 && !stillTyping && (
                        <div className="home-chatbot__msg-options">
                          {m.options.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              className="home-chatbot__msg-option"
                              onClick={() => void send(opt)}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Source citations intentionally hidden — the
                          internal labels (e.g. "FAQ tư vấn CDTB
                          [Cơ sở: admin-94]") are operator-facing and
                          shouldn't surface in the public bubble. */}
                      {/* Fee breakdown card for failed exam students */}
                      {m.feeData && !stillTyping && (
                        <div className="home-chatbot__fee-card">
                          <div className="home-chatbot__fee-card-header">
                            <i className="material-icons">receipt_long</i>
                            <span>Phí thi lại — {m.feeData.vehicleType === 'moto' ? 'Mô tô' : m.feeData.vehicleType === 'oto' ? 'Ô tô' : 'Xe'}</span>
                          </div>
                          <table className="home-chatbot__fee-table">
                            <thead>
                              <tr>
                                <th>Phần thi</th>
                                <th>Điểm</th>
                                <th>Phí (VNĐ)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {m.feeData.breakdown.map((row) => (
                                <tr key={row.partCode}>
                                  <td>{row.partName}</td>
                                  <td className="home-chatbot__fee-score">{row.diem ?? '—'}</td>
                                  <td className="home-chatbot__fee-amount">{row.amount.toLocaleString('vi-VN')}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={2}><strong>Tổng cộng</strong></td>
                                <td className="home-chatbot__fee-total">{m.feeData.totalAmount.toLocaleString('vi-VN')} đ</td>
                              </tr>
                            </tfoot>
                          </table>
                          {m.feeData.bank && (
                            <div className="home-chatbot__fee-bank">
                              <p className="home-chatbot__fee-bank-name">
                                <i className="material-icons">account_balance</i>
                                {m.feeData.bank.bankName} — {m.feeData.bank.accountNumber}
                              </p>
                              <p className="home-chatbot__fee-bank-holder">{m.feeData.bank.accountHolder}</p>
                            </div>
                          )}
                          {m.feeData.qrUrl && (
                            <div className="home-chatbot__fee-qr">
                              <p className="home-chatbot__fee-qr-label">Quét mã QR để thanh toán</p>
                              <img
                                src={m.feeData.qrUrl}
                                alt={`QR chuyển khoản ${m.feeData.totalAmount.toLocaleString('vi-VN')} VNĐ`}
                                className="home-chatbot__fee-qr-img"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {/* Retry chip on error bubbles — re-sends the failed
                          query as if the user re-typed it. */}
                      {m.error && m.failedQuery && (
                        <div className="home-chatbot__msg-options">
                          <button
                            type="button"
                            className="home-chatbot__msg-option home-chatbot__msg-option--retry"
                            onClick={() => void send(m.failedQuery!)}
                          >
                            <i className="material-icons">refresh</i> Thử lại
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={m.id}
                  className={`home-chatbot__msg home-chatbot__msg--user${continuation ? ' home-chatbot__msg--continuation' : ''}`}
                >
                  {!continuation && (
                    <div className="home-chatbot__msg-avatar home-chatbot__msg-avatar--user">
                      <i className="material-icons">person</i>
                    </div>
                  )}
                  {continuation && <div style={{ width: 24, flexShrink: 0 }} />}
                  <div className="home-chatbot__msg-bubble home-chatbot__msg-bubble--user">
                    <p>{m.text}</p>
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="home-chatbot__msg home-chatbot__msg--bot">
                <div className="home-chatbot__msg-avatar home-chatbot__msg-avatar--bot">
                  <img src={logo} alt="Bot" />
                </div>
                <div className="home-chatbot__loading" aria-label="Đang trả lời">
                  <span /><span /><span />
                </div>
              </div>
            )}
          </div>

          {/* Quick actions — suggested questions. Hidden when the bot has
              no training data so we don't tease unanswerable prompts.
              Drag-to-scroll: click + drag horizontally to reveal more
              chips that overflow the panel width. */}
          {showQuickActions && (
            <div
              ref={quickActionsRef}
              className="home-chatbot__quick-actions"
              onPointerDown={onChipsPointerDown}
              onPointerMove={onChipsPointerMove}
              onPointerUp={onChipsPointerEnd}
              onPointerCancel={onChipsPointerEnd}
              onPointerLeave={onChipsPointerEnd}
            >
              {QUICK_ACTIONS.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  className={`home-chatbot__quick-chip${q.accent ? ' home-chatbot__quick-chip--accent' : ''}`}
                  onClick={onChipClick(q.text)}
                >
                  {q.text}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <div className="home-chatbot__composer">
            <div className="home-chatbot__composer-row">
              <textarea
                ref={inputRef}
                className="home-chatbot__composer-input"
                placeholder="Nhập câu hỏi của bạn…"
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                type="button"
                className="home-chatbot__composer-send"
                onClick={() => send()}
                disabled={!input.trim()}
                aria-label="Gửi"
              >
                <i className="material-icons">send</i>
              </button>
            </div>
            <p className="home-chatbot__composer-disclaimer">
              AI có thể mắc lỗi — hãy kiểm tra lại thông tin quan trọng.
            </p>
          </div>
        </div>
      )}

      {!open && (
        <button
          type="button"
          className="home-chatbot__bubble"
          onClick={() => setOpen(true)}
          aria-label="Mở Trợ lý AI"
        >
          <img src={logo} alt="Trợ lý AI" className="home-chatbot__bubble-logo" />
          <span className="home-chatbot__bubble-badge">Hỏi AI</span>
        </button>
      )}
    </div>
  );
};

export default HomeChatbot;
